/**
 * Coordonnées de l'éditeur, telles qu'elles seront publiées.
 *
 * Les mentions légales et la politique de confidentialité sont rédigées ; il
 * leur manque trois informations que le code ne peut pas connaître. Elles sont
 * rassemblées ici plutôt que semées dans les textes : une valeur à compléter
 * qu'on cherche dans deux fichiers de chaînes traduites finit par n'être
 * complétée que d'un côté.
 *
 * **Rien n'est dû tant que le site n'est pas public** — ces obligations
 * naissent le jour de la mise en ligne. Mais un texte se rédige à froid, pas la
 * veille d'un déploiement, d'où les blancs plutôt que l'absence.
 *
 * `scripts/check-release.sh` refuse une publication tant qu'il en reste un.
 * Il n'est **pas** appelé par `scripts/verify.sh`, qui doit rester vert
 * pendant tout le développement : ce n'est pas un défaut, c'est une échéance.
 */

/**
 * Marque d'une valeur manquante.
 *
 * Volontairement voyante et sans ambiguïté : un blanc discret — une chaîne
 * vide, un tiret — se publierait sans que personne ne le voie.
 */
export const LEGAL_PLACEHOLDER = 'À COMPLÉTER'

export interface PublisherDetails {
  /** Adresse à laquelle un droit RGPD s'exerce. Sans elle, aucun n'est exerçable. */
  readonly contact: string
  /** Raison sociale et adresse de l'hébergeur, exigées par la LCEN (art. 6-III). */
  readonly host: string
  /**
   * Durée de conservation des journaux du serveur.
   *
   * Elle est fixée par l'hébergeur, pas par l'application : c'est une valeur à
   * lui demander, non à décider.
   */
  readonly logRetention: string
}

export const PUBLISHER: PublisherDetails = {
  contact: 'info@corentin.click',
  // Relevé le 22 août 2026 sur infomaniak.com et recoupé au registre du
  // commerce genevois (CHE-103.167.648). La valeur s'insère dans une phrase —
  // « Hébergeur : … » —, d'où l'absence de point final.
  host: 'Infomaniak Network SA, Rue Eugène-Marziano 25, 1227 Les Acacias (Genève), Suisse',
  // Constaté le 22 août 2026 dans l'espace de gestion Infomaniak, qui met un
  // mois de journaux à disposition — et non lu dans un engagement écrit de
  // l'hébergeur. S'insère entre parenthèses : « … la politique de l'hébergeur
  // (un mois) ».
  logRetention: 'un mois',
}

/** Reste-t-il une valeur à compléter ? */
export function missingPublisherDetails(
  details: PublisherDetails = PUBLISHER,
): readonly (keyof PublisherDetails)[] {
  return (Object.keys(details) as (keyof PublisherDetails)[]).filter(
    (key) => details[key].includes(LEGAL_PLACEHOLDER),
  )
}
